import React, { useEffect, useState } from "react";
import {
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Spinner
} from "reactstrap";
import { RiFileListLine } from "react-icons/ri";
import { savePlaylistAsTemplate } from "../helpers/UserTemplateHelpers";

const getDefaultVisibility = () => {
    const customer = JSON.parse(sessionStorage.getItem("customer") || "null");
    return customer?.user?.accountId ? "ACCOUNT" : "PRIVATE";
};

const hasAccountMembership = () => {
    const customer = JSON.parse(sessionStorage.getItem("customer") || "null");
    return Boolean(customer?.user?.accountId);
};

export default function SaveAsTemplateModal({ isOpen, toggle, playlistId, playlistName, onSaved }) {
    const [name, setName] = useState(playlistName || "");
    const [description, setDescription] = useState("");
    const [visibility, setVisibility] = useState(getDefaultVisibility());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const accountMember = hasAccountMembership();

    useEffect(() => {
        if (isOpen) {
            setName(playlistName || "");
            setDescription("");
            setVisibility(getDefaultVisibility());
            setError(null);
            setSaving(false);
        }
    }, [isOpen, playlistName]);

    const trackEvent = (eventName, params = {}) => {
        if (typeof window.gtag === "function") {
            window.gtag("event", eventName, params);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!name || !name.trim()) {
            setError("Template name is required");
            return;
        }

        setSaving(true);
        setError(null);
        const selectedVisibility = accountMember ? visibility : "PRIVATE";
        const result = await savePlaylistAsTemplate(playlistId, {
            name,
            description,
            visibility: selectedVisibility
        });

        setSaving(false);
        if (!result) {
            setError("Unable to save this service plan as a template.");
            return;
        }

        trackEvent("service_plan_saved_as_template", {
            template_id: result.id,
            visibility: selectedVisibility
        });
        if (selectedVisibility === "PRIVATE") {
            trackEvent("service_plan_template_created_private", { template_id: result.id });
        }
        if (selectedVisibility === "ACCOUNT") {
            trackEvent("service_plan_template_created_account", { template_id: result.id });
        }
        onSaved(result);
    };

    return (
        <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                <span style={{fontSize: "1rem"}}>Save as Template</span>
            </ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label for="save-template-name">Template name</Label>
                        <Input
                            id="save-template-name"
                            required
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="save-template-description">Description</Label>
                        <Input
                            id="save-template-description"
                            type="textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="save-template-visibility">Visibility</Label>
                        <Input
                            id="save-template-visibility"
                            type="select"
                            value={accountMember ? visibility : "PRIVATE"}
                            disabled={!accountMember}
                            onChange={(e) => setVisibility(e.target.value)}
                        >
                            <option value="ACCOUNT">Account</option>
                            <option value="PRIVATE">Private</option>
                        </Input>
                    </FormGroup>
                    {error && <p className="text-danger mb-3">{error}</p>}
                    <div className="text-right">
                        <Button color="secondary" type="button" onClick={toggle} disabled={saving}>
                            Cancel
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" /> : <RiFileListLine size={18} />}
                            <span className="btn-inner--text ml-2">Save as Template</span>
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
}
